import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import MusicPlayer from './MusicPlayer';
import './Layout.css';

const INTRO_LOGO_SRC = '/Design/LogoAlpha.png';

const Layout = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIntroLocked, setIsIntroLocked] = useState(false);
  const [isIntroComplete, setIsIntroComplete] = useState(!isHomePage);
  const headerRef = useRef(null);
  const navRef = useRef(null);

  // Reset navigation scroll position to start (fix mobile cutoff issue)
  useEffect(() => {
    if (navRef.current) {
      navRef.current.scrollLeft = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    // Scroll to top on page load/refresh
    window.scrollTo(0, 0);

    createParticles();

    const header = headerRef.current;
    if (!header) return;
    let introTimeout;

    const lockInteractions = () => {
      setIsIntroLocked(true);
      document.body.classList.add('intro-locked');
    };

    const unlockInteractions = () => {
      setIsIntroLocked(false);
      document.body.classList.remove('intro-locked');
    };

    const enterCompactState = () => {
      header.classList.add('scrolled');
      header.classList.remove('intro');
    };

    if (!isHomePage) {
      enterCompactState();
      setIsIntroComplete(true);
    } else {
      header.classList.add('intro');
      header.classList.remove('scrolled');
      lockInteractions();
      setIsIntroComplete(false);

      introTimeout = setTimeout(() => {
        unlockInteractions();
        enterCompactState();
        setIsIntroComplete(true);
      }, 3000);
    }

    const handleModalOpen = () => setIsModalOpen(true);
    const handleModalClose = () => {
      setTimeout(() => setIsModalOpen(false), 1000);
    };

    window.addEventListener('modal-opened', handleModalOpen);
    window.addEventListener('modal-closed', handleModalClose);

    return () => {
      window.removeEventListener('modal-opened', handleModalOpen);
      window.removeEventListener('modal-closed', handleModalClose);
      if (introTimeout) {
        clearTimeout(introTimeout);
      }
      unlockInteractions();
    };
  }, [isHomePage]);

  // Reset modal state on route change
  useEffect(() => {
    setIsModalOpen(false);
  }, [location.pathname]);

  const createParticles = () => {
    const particleContainer = document.getElementById('particles');
    if (!particleContainer || particleContainer.children.length > 0) return;

    // Reduce particles on mobile for better performance, throttle on desktop
    const isMobile = window.innerWidth <= 768;
    const particleCount = isMobile ? 60 : 80;

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'particle';
      
      const size = Math.random() * 8 + 3; // Increased from (4 + 2) to (8 + 3)
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 4}s`; // Increased variation
      particle.style.animationDuration = `${Math.random() * 3 + 2}s`; // Increased duration range
      
      particleContainer.appendChild(particle);
    }
  };

  return (
    <div className="app-container">
      <div className="particle-container" id="particles"></div>
      {isIntroLocked && <div className="intro-overlay" aria-hidden="true"></div>}
      
      <header ref={headerRef} className={`header ${isModalOpen ? 'header-hidden' : ''}`}>
        <div className="header-top-row">
          <MusicPlayer />
          <div className="wallet-button-wrapper">
            <WalletMultiButton />
          </div>
        </div>

        <nav className="navigation" ref={navRef}>
          <div className="nav-buttons-center">
            <Link to="/" className={`nav-btn ${location.pathname === '/' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Home</span>
            </Link>
            
            <Link to="/shop" className={`nav-btn ${location.pathname === '/shop' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span>Store</span>
            </Link>

            <Link to="/vote" className={`nav-btn ${location.pathname === '/vote' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span>Vote</span>
            </Link>

            <Link to="/create" className={`nav-btn ${location.pathname === '/create' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"></path>
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
              <span>Create</span>
            </Link>

            <Link to="/auction" className={`nav-btn ${location.pathname === '/auction' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 9l-3.3-3.3c-.4-.4-.9-.7-1.4-.7H7c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V10.7c0-.5-.2-1-.7-1.4z"></path>
                <path d="M13 3v4c0 1.1.9 2 2 2h4"></path>
              </svg>
              <span>Auction</span>
            </Link>

            <Link to="/faq" className={`nav-btn ${location.pathname === '/faq' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>FAQ</span>
            </Link>

            <Link to="/donate" className={`nav-btn ${location.pathname === '/donate' ? 'active' : ''}`}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
              <span>Donate</span>
            </Link>
          </div>
        </nav>

        <img src={INTRO_LOGO_SRC} alt="Popperverse Logo" className="logo" />
      </header>

      <main
        className={`main-content ${isIntroComplete ? 'content-visible' : ''}`}
        aria-hidden={isIntroLocked}
      >
        <div className="page-logo-container">
          <img src="/Design/LogoAlpha.png" alt="Popperverse" className="page-logo" />
        </div>
        {children}
      </main>
    </div>
  );
};

export default Layout;
